import React, { useMemo, useState } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import './chart.css';
import FlexComponent from '../FlexComponent';

const ALL_LINES = [
    { dataKey: 'Leads', stroke: 'rgb(15 45 62)', label: 'Leads' },
    { dataKey: 'Replies', stroke: '#da474e', label: 'Replies' },
    { dataKey: 'Outbound Calls', stroke: '#D3D3D3', label: 'Outbound Calls' },
    { dataKey: 'App Count', stroke: '#898fd3', label: 'App Count' },
    { dataKey: 'Total Docs In', stroke: '#777b92', label: 'Total Docs In' },
    { dataKey: 'Pre Approvals', stroke: '#eeabae', label: 'Pre Approvals' },
    { dataKey: 'Shoppers', stroke: '#86acd1', label: 'Shoppers' },
    { dataKey: 'Open', stroke: '#95d186', label: 'Open' },
    { dataKey: 'Closed', stroke: '#f5ce42', label: 'Closed' },
];

export default function ReportingLineChart({
    label,
    leads,
    replies,
    oppertunities,
    preApps,
    shoppers,
    outboundCalls,
    chartAppLeads,
    chartOpenApps,
    chartClosedApps,
    loading,
}) {
    // Transform props into Recharts data format
    const data = useMemo(() => {
        return label.map((lbl, i) => ({
            label: lbl,
            Leads: leads[i] ?? 0,
            Replies: replies[i] ?? 0,
            'Outbound Calls': outboundCalls[i] ?? 0,
            'App Count': chartAppLeads[i] ?? 0,
            'Total Docs In': oppertunities[i] ?? 0,
            'Pre Approvals': preApps[i] ?? 0,
            Shoppers: shoppers[i] ?? 0,
            'Open': chartOpenApps[i] ?? 0,
            'Closed': chartClosedApps[i] ?? 0,
        }));
    }, [label, leads, replies, oppertunities, preApps, shoppers, outboundCalls, chartAppLeads]);

    // Toggle visibility state
    const [visibleLines, setVisibleLines] = useState(new Set(ALL_LINES.map(l => l.dataKey)));

    const toggleLine = (key) => {
        setVisibleLines(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const showAll = () => setVisibleLines(new Set(ALL_LINES.map(l => l.dataKey)));
    const hideAll = () => setVisibleLines(new Set());

    return (
        <div style={{ position: 'relative', minHeight: '460px', background: "black" }}>
            {/* Loading Overlay */}
            {loading && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'black',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                        borderRadius: '8px',
                    }}
                >
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2.5rem', color: 'white' }} />
                </div>
            )}

            {/* Toggle Stats Bar */}
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    background: 'black',
                    borderBottom: '1px solid #e5e7eb',
                }}
            >
                {/* <button
          onClick={showAll}
          style={{
            fontSize: '0.75rem',
            padding: '0.25rem 0.5rem',
            background: '#10b981',
            color: '#fff',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Show all
        </button>
        <button
          onClick={hideAll}
          style={{
            fontSize: '0.75rem',
            padding: '0.25rem 0.5rem',
            background: '#ef4444',
            color: '#fff',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Hide all
        </button> */}

                <FlexComponent justifyContent="center" gap="1rem" flexWrap="wrap" >
                    {ALL_LINES.map(({ dataKey, stroke, label: txt }) => {
                        const active = visibleLines.has(dataKey);
                        return (
                            <div  key={dataKey} style={{display : "flex", cursor: 'default'}} onClick={() => toggleLine(dataKey)} >
                                <div
                                   
                                    style={{
                                        padding: '4px 1.5rem',
                                        background: stroke,
                                        color: active ? '#fff' : '#374151',
                                        // borderRadius: '4px',
                                        border: 'none',
                                        transition: 'background .2s',
                                        cursor: 'default'
                                    }}
                                >
                                </div>
                                 <div style={{color: "white",  textDecoration : active ? "auto" : 'line-through', marginLeft:"3px", fontSize: "12px", fontWeight: "500" }} >{txt}</div> 
                            </div>
                        );
                    })}
                </FlexComponent>
            </div>

            {/* Recharts Line Chart */}
            <ResponsiveContainer width="100%" height={500}>
                <LineChart data={data} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
                    <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" vertical={false} />

                    <XAxis
                        dataKey="label"
                        stroke="#6b7280"
                        fontSize={12}
                        tickLine={false}
                        axisLine={{ stroke: '#6b7280', strokeWidth: 1 }}
                    />

                    <YAxis
                        stroke="#6b7280"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v) => v}
                    />

                    <Tooltip
                        contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            fontSize: '13px',
                        }}
                        labelStyle={{ color: '#1f2937', fontWeight: 'bold' }}
                        cursor={{ stroke: '#9ca3af', strokeWidth: 1, strokeDasharray: '5 5' }}
                    />

                    {/* <Legend
            wrapperStyle={{ paddingTop: '20px', fontSize: '13px', color: '#4b5563' }}
            iconType="line"
          /> */}

                    {ALL_LINES.map(({ dataKey, stroke }) =>
                        visibleLines.has(dataKey) ? (
                            <Line
                                key={dataKey}
                                type="monotone"
                                dataKey={dataKey}
                                stroke={stroke}
                                strokeWidth={2.5}
                                dot={{ r: 5, fill: stroke, strokeWidth: 2 }}
                                activeDot={{ r: 7, stroke: stroke, strokeWidth: 2 }}
                                isAnimationActive
                                connectNulls={false}
                            />
                        ) : null
                    )}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}